import { useEffect, useEffectEvent, useRef, type RefObject } from 'react';
import { useKeyedValue } from './use_keyed_value.js';

/** URLs que a pré-carga já trouxe, para não pedir de novo. Fica no módulo, não
 *  em estado: o `load` de foto como update do React reiniciava renders
 *  concorrentes enquanto as fotos pingavam da rede (a lição do `VeiledImage`). */
const preloaded = new Set<string>();
/** Pedidos em voo: a referência segura a `Image` até o fim, senão o coletor
 *  pode levá-la e o navegador cancelar o pedido no meio. */
const inFlight = new Map<string, HTMLImageElement>();

function preload(url: string): void {
  if (!url || preloaded.has(url) || inFlight.has(url) || typeof Image !== 'function') return;
  const image = new Image();
  image.decoding = 'async';
  image.onload = () => {
    inFlight.delete(url);
    preloaded.add(url);
  };
  // Erro na pré-carga não marca a foto como falha: quem decide é a `img` que
  // aparece, com o tratamento por URL de sempre.
  image.onerror = () => {
    inFlight.delete(url);
  };
  inFlight.set(url, image);
  image.src = url;
}

function preloadNeighbors(urls: readonly string[], index: number): void {
  const total = urls.length;
  if (total < 2) return;
  for (const delta of [1, -1]) preload(urls[(((index + delta) % total) + total) % total] ?? '');
}

/**
 * Pré-carrega as fotos vizinhas (n±1, com volta) da foto corrente de um
 * carrossel, para o avanço não mostrar o esqueleto enquanto a próxima baixa
 * (#5616). Carrossel de uma foto só não pede nada.
 *
 * Com `target`, a pré-carga espera o elemento entrar na viewport: numa lista de
 * cards, só quem a pessoa vê baixa as vizinhas, e quem fica fora da tela segue
 * só com a foto `lazy` de sempre. Sem `target` (um card só, como a prévia do
 * pino no mapa), pré-carrega já. A URL tem que ser a MESMA que a `img` pede,
 * senão o cache não serve.
 */
export function useNeighborPreload(
  urls: readonly string[],
  index: number,
  target?: RefObject<Element | null>,
): void {
  const seen = useRef(!target);
  // The list arrives new on every render; it only changes identity with its content.
  const shownUrls = useKeyedValue(urls, urls.join('\n'));
  const preloadCurrent = useEffectEvent(() => {
    preloadNeighbors(shownUrls, index);
  });

  useEffect(() => {
    if (seen.current) preloadNeighbors(shownUrls, index);
  }, [shownUrls, index]);

  useEffect(() => {
    const node = target?.current;
    if (!node || seen.current || typeof IntersectionObserver !== 'function') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      seen.current = true;
      observer.disconnect();
      preloadCurrent();
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [target]);
}
